import { useState, type ReactNode } from "react";
import { ChartBarIcon } from "@phosphor-icons/react/ChartBar";
import { DownloadSimpleIcon } from "@phosphor-icons/react/DownloadSimple";
import { BarChart } from "@freightsimple/ui-core/Display/BarChart";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlBarItem } from "@freightsimple/ui-core/PageLayout/ControlBarItem";
import { DateRangePicker } from "@freightsimple/ui-core/DateTime/Controls/DateRangePicker";
import { DonutChart } from "@freightsimple/ui-core/Display/DonutChart";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Page } from "@freightsimple/ui-core/PageLayout/Page";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { StackedTextCell } from "@freightsimple/ui-core/Cells/StackedTextCell";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tab } from "@freightsimple/ui-core/PageLayout/Tab";
import { Tabs } from "@freightsimple/ui-core/PageLayout/Tabs";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";
import { toast } from "sonner";
import { type PerformanceResponse } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceResponse";
import { type PerformanceLaneRow } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceLaneRow";
import { type PortalCurrency } from "@freightsimple/generated-carriers-openapi-client/models/PortalCurrency";
import {
  formatLocalDate,
  todayMinus,
  today,
} from "@freightsimple/time/helpers";
import { type LocalDate } from "@freightsimple/time/branded-types";
import { downloadBase64File } from "@/App/Opportunities/downloadBase64File";
import { carriersApi } from "@/App/Auth/carrierApi";
import { CarrierLoadFailed } from "@/App/CarrierLoadFailed";
import { useCarrierFetch } from "@/App/useCarrierFetch";
import { CompetitivenessPanel } from "@/App/Performance/CompetitivenessPanel";
import { ServiceQualityPanel } from "@/App/Performance/ServiceQualityPanel";
import { FreightProfileGrid } from "@/App/Performance/FreightProfileGrid";
import { BillingHygienePanel } from "@/App/Performance/BillingHygienePanel";
import { LaneDrillDownDialog } from "@/App/Performance/LaneDrillDownDialog";
import { PerformanceWindow } from "@/App/Performance/PerformanceWindow";
import { formatMoney, formatPercent } from "@/App/Views/carrierFormatting";
import { CarrierPageLogo } from "@/App/CarrierPageLogo";
import { useCarrierIdentity } from "@/App/Auth/useCarrierIdentity";

// Chart colors, matching the carrier-portal palette (nav blue for "ours",
// muted slate for the comparison series).
const QUOTED = "#cbd5e1";
const BOOKED = "#4c62ea";
const ONTIME = "#22c55e";
const LATE = "#ef4444";
const RATE = "#e8a85d";
const DONUT_PALETTE = [
  "#4c62ea",
  "#38bdf8",
  "#22c55e",
  "#e8a85d",
  "#a855f7",
  "#f97316",
  "#14b8a6",
  "#94a3b8",
];

function formatDays(days: number): string {
  if (!Number.isFinite(days)) return "—";
  return `${days.toFixed(1)} days`;
}

/**
 * A chart title that names its currency, when we know it.
 *
 * Interpolating the currency straight into a template literal printed
 * "Spend (undefined)" whenever the response didn't carry one. The API types the
 * field as required, so TypeScript can't catch that — only a response that
 * doesn't match its own contract can, which is exactly when a carrier must not
 * be shown the word "undefined".
 */
function withCurrency_(title: string, currency: PortalCurrency | undefined) {
  return currency === undefined ? title : `${title} (${currency})`;
}

// Money in the width of one bar. The full figure, with its currency, is in the tooltip.
function compactMoney_(amount: number): string {
  if (amount >= 1000) return `$${Math.round(amount / 1000)}k`;
  return `$${Math.round(amount)}`;
}

export function PerformanceView() {
  // A year, because most of this page is month-by-month and a quarter shows three
  // or four bars — too few to read a trend from, and too few for a rate on a thin
  // month to mean anything. Still only a starting point: carriers argue about
  // specific months, which is why the range is theirs to change.
  const [range, setRange] = useState<[LocalDate, LocalDate]>([
    todayMinus(12, "month"),
    today(),
  ]);
  const [exporting, setExporting] = useState(false);

  // A LocalDate is already the YYYY-MM-DD the API wants. formatLocalDate is a
  // display formatter, and running the range through it sent "Fri May 1st".
  const fromDate = range[0];
  const toDate = range[1];

  const { state, refreshing, refresh } = useCarrierFetch(
    () => carriersApi().getCarrierPerformance({ fromDate, toDate }),
    `${fromDate}|${toDate}`,
  );
  const data = state.status === "loaded" ? state.data : undefined;

  async function exportCsv() {
    setExporting(true);
    try {
      const file = await carriersApi().exportCarrierPerformance({
        fromDate,
        toDate,
      });
      downloadBase64File(file.fileName, file.contentType, file.base64Content);
    } catch {
      toast.error("Couldn't build the export. Please try again.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <Page
      leadingVisual={<CarrierPageLogo />}
      icon={ChartBarIcon}
      title="Performance"
      description={`Your service performance with FreightSimple, ${formatLocalDate(fromDate, "MMM D")} to ${formatLocalDate(toDate, "MMM D, YYYY")}.`}
      refreshing={refreshing}
      headerActions={
        <>
          <ControlBarItem label="Dates">
            <DateRangePicker
              value={range}
              onChange={(next) => {
                if (next) setRange(next);
              }}
            />
          </ControlBarItem>
          {/* The export is how a carrier checks our arithmetic against their own
              system rather than taking the summary on trust. */}
          <Button
            icon={DownloadSimpleIcon}
            onClick={exportCsv}
            loading={exporting}
            tooltip="Download the shipments behind these figures"
            data-testid="export-performance-button"
          >
            Export
          </Button>
        </>
      }
    >
      {state.status === "loading" && (
        <T size="sm" color="secondary">
          Loading…
        </T>
      )}
      {state.status === "error" && (
        <CarrierLoadFailed what="your performance" onRetry={refresh} />
      )}
      {data && (
        <Tabs>
          {/* Every tab restates the window it was computed over. The header's
              picker scrolls out of view; the numbers don't. */}
          <Tab id="overview" title="Overview">
            <Stack gap={3}>
              <PerformanceWindow range={range} />
              <OverviewSection_ data={data} />
            </Stack>
          </Tab>
          {data.competitiveness && (
            <Tab id="competitiveness" title="Price Position">
              <Stack gap={3}>
                <PerformanceWindow range={range} />
                <CompetitivenessPanel competitiveness={data.competitiveness} />
              </Stack>
            </Tab>
          )}
          <Tab id="volume" title="Volume & Win Rate">
            <Stack gap={3}>
              <PerformanceWindow range={range} />
              <VolumeSection_ data={data} />
            </Stack>
          </Tab>
          <Tab id="ontime" title="On-Time">
            <Stack gap={3}>
              <PerformanceWindow range={range} />
              <OnTimeSection_ data={data} />
            </Stack>
          </Tab>
          {data.serviceQuality && (
            <Tab id="service" title="Service Quality">
              <Stack gap={3}>
                <PerformanceWindow range={range} />
                <ServiceQualityPanel serviceQuality={data.serviceQuality} />
              </Stack>
            </Tab>
          )}
          {data.freightProfile && (
            <Tab id="freight" title="Freight Profile">
              <Stack gap={3}>
                <PerformanceWindow range={range} />
                <FreightProfileGrid freightProfile={data.freightProfile} />
              </Stack>
            </Tab>
          )}
          {(data.billingHygiene ?? data.cspUtilisation) && (
            <Tab id="billing" title="Billing & Pricing">
              <Stack gap={3}>
                <PerformanceWindow range={range} />
                <BillingHygienePanel
                  billingHygiene={data.billingHygiene}
                  cspUtilisation={data.cspUtilisation}
                />
              </Stack>
            </Tab>
          )}
          <Tab id="cargo" title="Cargo & Lanes">
            <Stack gap={3}>
              <PerformanceWindow range={range} />
              <CargoSection_ data={data} fromDate={fromDate} toDate={toDate} />
            </Stack>
          </Tab>
        </Tabs>
      )}
    </Page>
  );
}

function OverviewSection_({ data }: { data: PerformanceResponse }) {
  const { kpis } = data;
  // Named, because these are this carrier's numbers with FreightSimple. A bare
  // "Win Rate" on a FreightSimple-branded page reads as FreightSimple's.
  const { carrierName } = useCarrierIdentity();
  return (
    <Stack gap={4}>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <StatCard_
          label={`Booked with ${carrierName}`}
          value={kpis.bookedShipments.toLocaleString()}
          explanation="Shipments booked with you, counted by the date they were booked — not the date they were quoted. Every other figure on this tile row describes this same set of shipments."
        />
        <StatCard_
          label={`${carrierName} Win Rate`}
          value={formatPercent(kpis.winRate)}
          explanation="Of the distinct shipments you priced in this window, the share the customer booked with you. Every shipment you quoted counts, including ones nobody booked. Re-quotes of the same real shipment count once. This is counted by quote date, so it won't divide neatly into the Booked figure."
        />
        <StatCard_
          label={`${carrierName} On-Time`}
          value={formatPercent(kpis.onTimeDeliveryRate)}
          explanation="Of your booked shipments that have delivered, the share that arrived no later than the latest delivery date on the quote. That date is FreightSimple's estimate, not a transit time you gave us: we treat your transit time as the earliest possible day and predict a later one, aiming for most shipments to arrive inside the window. Counted in business days from pickup. Shipments still moving, and any we have no delivery date for, are left out of both sides."
        />
        <StatCard_
          label={`${carrierName} Avg Transit`}
          value={formatDays(kpis.averageTransitDays)}
          explanation="Average business days from pickup to delivery across your booked shipments that have delivered. Weekends and holidays are excluded."
        />
        <StatCard_
          label={`Spend with ${carrierName}`}
          value={formatMoney(kpis.totalSpend, data.currency)}
          explanation="What these shipments cost us, before tax: the agreed rate until you invoice us, then your invoices less any credit notes. Excludes insurance, which never goes to you. Called spend rather than paid because nothing has been paid on a shipment still moving."
        />
        <StatCard_
          label="Avg / Shipment"
          value={formatMoney(kpis.averageSpendPerShipment, data.currency)}
          explanation="Total Spend divided by Booked. A blended average across every lane and service in the window, so it moves with freight mix as much as with price."
        />
      </div>
      <MonthlySections_ data={data} />
    </Stack>
  );
}

/**
 * The Overview's month-by-month picture, in three sections.
 *
 * Grouped rather than stacked as one long strip of charts, because the three answer different
 * questions: how much freight there was, how much of it was won, and what it cost us. A carrier
 * arrives with one of those three in mind.
 */
function MonthlySections_({ data }: { data: PerformanceResponse }) {
  const months = data.volumeByMonth;
  const labels = months.map((m) => m.month);
  const empty = labels.length === 0;

  return (
    <Stack gap={4}>
      <Panel title="Volume">
        <Grid columns={2}>
          <ChartBlock_
            title="Quoted Shipments"
            empty={empty}
            explanation="Distinct shipments you returned a customer-visible quote for in each month, by quote date. Quoting one shipment on three services counts once."
          >
            <BarChart
              labels={labels}
              showValues
              series={[
                {
                  label: "Quoted",
                  color: QUOTED,
                  values: months.map((m) => m.quoted),
                },
              ]}
            />
          </ChartBlock_>
          <ChartBlock_
            title="Bookings"
            empty={empty}
            explanation="Shipments booked with you in each month, by booking date. Counted over a different set of freight from Quoted Shipments, which uses quote date."
          >
            <BarChart
              labels={labels}
              showValues
              series={[
                {
                  label: "Booked",
                  color: BOOKED,
                  values: months.map((m) => m.booked),
                },
              ]}
            />
          </ChartBlock_>
        </Grid>
      </Panel>

      <Panel title="Winning">
        <Grid columns={2}>
          <ChartBlock_
            title="Win Rate"
            empty={empty}
            explanation="Of the shipments you priced in each month, the share the customer booked with you. Every shipment quoted that month counts, including ones nobody booked. A month with nothing quoted shows no bar rather than zero."
          >
            <BarChart
              labels={labels}
              showValues
              formatValue={(v) => `${Math.round(v * 100)}%`}
              series={[
                {
                  label: "Win Rate",
                  color: RATE,
                  // Passed through as-is. A month with nothing quoted has no rate, and
                  // the chart draws a gap; a zero would say they lost everything.
                  values: months.map((m) => m.winRate),
                },
              ]}
            />
          </ChartBlock_>
          <ChartBlock_
            title="Average Rank"
            empty={empty}
            explanation="Your average position on the quote boards you appeared on that month, where 1 is cheapest. The scale runs best-at-the-top, so a shorter bar is a better month."
          >
            <BarChart
              labels={labels}
              showValues
              // 1 is the best a carrier can do, so the scale runs 1 at the top
              // down to the worst rank. A tall bar would otherwise mean a good
              // month, which is the opposite of what it is.
              invertY
              formatValue={(v) => v.toFixed(1)}
              series={[
                {
                  label: "Average Rank",
                  color: BOOKED,
                  values: months.map((m) => m.averagePriceRank),
                },
              ]}
            />
          </ChartBlock_>
        </Grid>
      </Panel>

      <Panel title="Spend">
        <ChartBlock_
          title={withCurrency_("Spend", data.currency)}
          empty={empty}
          explanation="What the shipments booked with you in each month cost us, before tax, by booking date. The agreed rate until you invoice us, then your invoices less any credit notes. Excludes insurance, which never goes to you."
        >
          <BarChart
            labels={labels}
            showValues
            formatValue={(v) => compactMoney_(v)}
            formatTooltipValue={(v) => formatMoney(v, data.currency)}
            series={[
              {
                label: "Spend",
                color: ONTIME,
                values: months.map((m) => m.spend),
              },
            ]}
          />
        </ChartBlock_>
      </Panel>
    </Stack>
  );
}

/**
 * A titled chart inside a section panel, or the empty message in its place.
 *
 * `explanation` is required for the same reason it is on PerformanceMetric: every
 * series here has a date anchor and a denominator that the title alone doesn't
 * give away, and a carrier who can't reproduce a bar assumes it is wrong.
 */
function ChartBlock_({
  title,
  empty,
  explanation,
  children,
}: {
  title: string;
  empty: boolean;
  explanation: string;
  children: ReactNode;
}) {
  return (
    <Stack gap={2}>
      <div className="flex items-center gap-1.5">
        <T size="sm" color="secondary">
          {title}
        </T>
        <Tooltip variant="info" size="sm" content={explanation} />
      </div>
      <ChartOrEmpty_ empty={empty}>{children}</ChartOrEmpty_>
    </Stack>
  );
}

function VolumeSection_({ data }: { data: PerformanceResponse }) {
  const labels = data.volumeByMonth.map((m) => m.month);
  return (
    <Stack gap={4}>
      <VolumeCharts_ data={data} />
      <Grid columns={2}>
        <Panel
          title="Win Rate by Month"
          description="Of the shipments you priced in each month, the share the customer booked with you. Counted by quote date. Deliberately not bookings divided by quoted shipments: bookings are counted by booking date, so the two describe different sets of freight and dividing one by the other produces a number that is neither."
        >
          <ChartOrEmpty_ empty={labels.length === 0}>
            <BarChart
              labels={labels}
              showValues
              series={[
                {
                  label: "Win Rate",
                  color: RATE,
                  values: data.volumeByMonth.map((m) => m.winRate),
                },
              ]}
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </ChartOrEmpty_>
        </Panel>
        <Panel
          title="Spend by Month"
          description="What the shipments booked with you in each month cost us, before tax, counted by booking date. The agreed rate until you invoice us, then your invoices less any credit notes."
        >
          <ChartOrEmpty_ empty={labels.length === 0}>
            <BarChart
              labels={labels}
              series={[
                {
                  label: "Spend",
                  color: BOOKED,
                  values: data.volumeByMonth.map((m) => m.spend),
                },
              ]}
              showValues
              formatValue={(v) => compactMoney_(v)}
              formatTooltipValue={(v) => formatMoney(v, data.currency)}
            />
          </ChartOrEmpty_>
        </Panel>
      </Grid>
    </Stack>
  );
}

function OnTimeSection_({ data }: { data: PerformanceResponse }) {
  const labels = data.onTimeByMonth.map((m) => m.month);
  return (
    <Stack gap={4}>
      <Grid columns={2}>
        <Panel
          title="On-Time vs Late by Month"
          description="Your delivered shipments each month, split by whether they arrived inside the quoted transit window. Counted by booking date. On time means arriving no later than the latest delivery date on the quote. That date is FreightSimple's estimate, not a transit time you gave us: we treat your transit time as the earliest possible day and predict a later one, aiming for most shipments to arrive inside the window. Shipments still moving, and any we have no delivery date for, appear in neither bar."
        >
          <ChartOrEmpty_ empty={labels.length === 0}>
            <BarChart
              labels={labels}
              stacked
              series={[
                {
                  label: "On Time",
                  color: ONTIME,
                  values: data.onTimeByMonth.map((m) => m.onTime),
                },
                {
                  label: "Late",
                  color: LATE,
                  values: data.onTimeByMonth.map((m) =>
                    Math.max(m.booked - m.onTime, 0),
                  ),
                },
              ]}
            />
          </ChartOrEmpty_>
        </Panel>
        <Panel
          title="On-Time Rate by Month"
          description="The same figures as a rate: on-time deliveries divided by delivered shipments, for each month. A month with nothing delivered has no rate rather than a zero."
        >
          <ChartOrEmpty_ empty={labels.length === 0}>
            <BarChart
              labels={labels}
              series={[
                {
                  label: "On-Time Rate",
                  color: ONTIME,
                  values: data.onTimeByMonth.map((m) => m.onTimeRate),
                },
              ]}
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </ChartOrEmpty_>
        </Panel>
      </Grid>
    </Stack>
  );
}

function CargoSection_({
  data,
  fromDate,
  toDate,
}: {
  data: PerformanceResponse;
  fromDate: string;
  toDate: string;
}) {
  const totalCargo = data.cargoProfile.reduce((sum, c) => sum + c.count, 0);
  return (
    <Stack gap={4}>
      <Grid columns={2}>
        <Panel
          title="Freight Class Mix"
          description="The freight classes across shipments booked with you in this window. Class is computed by us from the density the customer declared, not supplied by them."
        >
          {data.cargoProfile.length === 0 ? (
            <EmptyChart_ />
          ) : (
            <DonutChart
              centerLabel="Shipments"
              centerValue={totalCargo.toLocaleString()}
              slices={data.cargoProfile.map((c, i) => ({
                label: `Class ${c.freightClass}`,
                value: c.count,
                color: DONUT_PALETTE[i % DONUT_PALETTE.length]!,
              }))}
            />
          )}
        </Panel>
        <Panel
          title="Average Weight by Class"
          description="Average total shipment weight in each freight class, across shipments booked with you."
          fullBleed
        >
          <CargoTable_ data={data} />
        </Panel>
      </Grid>
      <Panel
        title="Lanes Serviced"
        description="Every lane you priced in this window, busiest first. Lane Volume is everything we quoted on that lane by any carrier, so participation reads as your share of the opportunity."
        fullBleed
      >
        <LanesTable_ data={data} fromDate={fromDate} toDate={toDate} />
      </Panel>
    </Stack>
  );
}

// Quotes and bookings live on very different scales (quotes in the hundreds/
// thousands, bookings in the tens), so they get one chart each — on a shared
// axis the booked bars would be invisible.
function VolumeCharts_({ data }: { data: PerformanceResponse }) {
  const labels = data.volumeByMonth.map((m) => m.month);
  const empty = labels.length === 0;
  return (
    <Grid columns={2}>
      <Panel
        title="Quotes by Month"
        description="Distinct shipments you returned a customer-visible quote for, by quote date. Quoting one shipment on three services counts once."
      >
        <ChartOrEmpty_ empty={empty}>
          <BarChart
            labels={labels}
            series={[
              {
                label: "Quoted",
                color: QUOTED,
                values: data.volumeByMonth.map((m) => m.quoted),
              },
            ]}
          />
        </ChartOrEmpty_>
      </Panel>
      <Panel
        title="Bookings by Month"
        description="Shipments booked with you, by booking date. A different set of freight from Quotes by Month above, which is counted by quote date."
      >
        <ChartOrEmpty_ empty={empty}>
          <BarChart
            labels={labels}
            series={[
              {
                label: "Booked",
                color: BOOKED,
                values: data.volumeByMonth.map((m) => m.booked),
              },
            ]}
          />
        </ChartOrEmpty_>
      </Panel>
    </Grid>
  );
}

function CargoTable_({ data }: { data: PerformanceResponse }) {
  const columns: Column<PerformanceResponse["cargoProfile"][number]>[] = [
    {
      key: "class",
      header: "Freight Class",
      render: (row) => <TextCell value={row.freightClass} />,
    },
    {
      key: "count",
      header: "Shipments",
      align: "right",
      render: (row) => <TextCell value={row.count.toLocaleString()} />,
    },
    {
      key: "weight",
      header: "Avg Weight",
      align: "right",
      render: (row) => (
        <TextCell
          value={`${Math.round(row.averageWeightLbs).toLocaleString()} lbs`}
        />
      ),
    },
  ];
  return (
    <Table
      columns={columns}
      data={data.cargoProfile}
      rowKey={(row) => row.freightClass}
      emptyMessage="No cargo booked in this period."
    />
  );
}

function LanesTable_({
  data,
  fromDate,
  toDate,
}: {
  data: PerformanceResponse;
  fromDate: string;
  toDate: string;
}) {
  const columns: Column<PerformanceLaneRow>[] = [
    {
      key: "lane",
      header: "Lane",
      render: (lane) => (
        <StackedTextCell primary={`${lane.pickup} → ${lane.delivery}`} />
      ),
    },
    {
      key: "quoted",
      header: "You Priced",
      align: "right",
      render: (lane) => <TextCell value={lane.quoted.toLocaleString()} />,
    },
    {
      key: "laneVolume",
      header: "Lane Volume",
      align: "right",
      // Everything we quote on the lane, including freight this carrier never
      // saw. It discloses our lane volumes, which is the accepted trade for
      // showing them where the freight they aren't winning actually is.
      render: (lane) => (
        <TextCell value={lane.laneOpportunityShipments?.toLocaleString()} />
      ),
    },
    {
      key: "booked",
      header: "Booked",
      align: "right",
      render: (lane) => <TextCell value={lane.booked.toLocaleString()} />,
    },
    {
      key: "winRate",
      header: "Win Rate",
      align: "right",
      render: (lane) => <TextCell value={formatPercent(lane.winRate)} />,
    },
    {
      key: "rank",
      header: "Avg Rank",
      align: "right",
      render: (lane) => <TextCell value={lane.averagePriceRank?.toFixed(1)} />,
    },
    {
      key: "premium",
      header: "Premium",
      align: "right",
      render: (lane) => (
        <TextCell
          value={
            lane.averagePremiumWhenNotCheapest === undefined
              ? undefined
              : formatPercent(lane.averagePremiumWhenNotCheapest)
          }
        />
      ),
    },
    {
      key: "avgPaid",
      header: "Avg Paid",
      align: "right",
      render: (lane) => (
        <TextCell
          value={
            lane.averageSpend === undefined
              ? undefined
              : formatMoney(lane.averageSpend)
          }
        />
      ),
    },
  ];
  return (
    <Table
      columns={columns}
      data={data.lanes}
      rowKey={(lane) => `${lane.pickup}->${lane.delivery}`}
      emptyMessage="Nothing quoted in this period."
      rowActions={(lane) => (
        <Button
          dialog={() => (
            <LaneDrillDownDialog
              lane={lane}
              fromDate={fromDate}
              toDate={toDate}
            />
          )}
        >
          Shipments
        </Button>
      )}
    />
  );
}

function ChartOrEmpty_({
  empty,
  children,
}: {
  empty: boolean;
  children: React.ReactNode;
}) {
  return empty ? <EmptyChart_ /> : <>{children}</>;
}

function EmptyChart_() {
  return (
    <T size="sm" color="secondary">
      No data for this period yet.
    </T>
  );
}

function StatCard_({
  label,
  value,
  explanation,
}: {
  label: string;
  value: string;
  explanation: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-1.5">
        <span className="text-xs tracking-wide text-content-muted uppercase">
          {label}
        </span>
        {/* Every tile carries its own rule. A carrier who can't reproduce a
            figure from their own records assumes it's wrong, and the one that
            actually confused people was Win Rate — its denominator isn't the
            bookings chart beside it. */}
        <Tooltip variant="info" size="sm" content={explanation} />
      </div>
      <div className="mt-1 text-lg font-bold text-foreground">{value}</div>
    </div>
  );
}
